// embedded google apps + external sheet links. google calendar and google sheets embed as
// iframes; gmail and smartsheet set x-frame-options
// and cannot be framed, so those launch in a new tab.

import { useState, type ReactNode } from 'react';
import { LoadingState, PageHeader, PageHelpButton, Spinner } from '../components/ui.js';
import { useAuth } from '../auth/AuthContext.js';
import { useIsPhone } from '../lib/media.js';

const SHEET_PREVIEW = (id: string) => `https://docs.google.com/spreadsheets/d/${id}/preview`;
const SHEET_EDIT = (id: string) => `https://docs.google.com/spreadsheets/d/${id}/edit`;
const SS = (id: string) => `https://app.smartsheet.com/sheets/${id}`;

function LinkCard({ title, url, hint }: { title: string; url: string; hint?: string }) {
  return (
    <a className="page-panel" href={url} target="_blank" rel="noopener noreferrer" style={{ display: 'block', textDecoration: 'none', color: 'var(--text)' }}>
      <div style={{ fontFamily: 'var(--font-disp)', fontWeight: 700, fontSize: 14 }}>{title} ↗</div>
      <div className="muted">{hint ?? 'Open in a new tab'}</div>
    </a>
  );
}

/** set of google sheets embedded read-only, with a tab selector + edit-in-new-tab. */
function SheetTabs({ title, sheets }: { title: string; sheets: { title: string; id: string }[] }) {
  const [i, setI] = useState(0);
  // keyed to the sheet id, so switching tabs (which remounts the iframe) shows the overlay again.
  const [loadedId, setLoadedId] = useState<string | null>(null);
  const active = sheets[i]!;
  return (
    <div className="embed">
      <div className="embed-bar">
        <h1 className="page-title">{title}</h1>
        <div className="toolbar" style={{ margin: 0, gap: 6 }}>
          {sheets.map((s, idx) => (
            <button type="button" key={s.id} className={`chip ${i === idx ? 'active' : ''}`} aria-pressed={i === idx} onClick={() => setI(idx)}>{s.title}</button>
          ))}
        </div>
        <div style={{ flex: 1 }} />
        <span className="muted embed-hint">Blank? Sign into Google in this browser<span className="embed-hint-arrow" aria-hidden="true"> →</span></span>
        <a className="btn btn-sm" href={SHEET_EDIT(active.id)} target="_blank" rel="noopener noreferrer">Open to edit ↗</a>
        <PageHelpButton />
      </div>
      <div className="embed-body">
        {loadedId !== active.id && (
          <div className="embed-loading"><Spinner /><span>Loading sheet…</span></div>
        )}
        <iframe key={active.id} className="embed-frame" src={SHEET_PREVIEW(active.id)} title={active.title} onLoad={() => setLoadedId(active.id)} />
      </div>
    </div>
  );
}

export function ChangeLog() {
  return <SheetTabs title="Change Log" sheets={[{ title: 'Sales Change Log', id: '1Mvwd9aw6Mug1y4G97qfJ44m_PipuUYNJVfE_oLlQq14' }]} />;
}

export function OrderSheets() {
  return (
    <SheetTabs
      title="Order Sheets"
      sheets={[
        { title: 'Orders 26-27', id: '1yxq_bL9oMgbILMtt2pdSHD12LE1FUXGFMP8b2L0mKQc' },
        { title: '40 / 50', id: '1Z7IPQlI6pY77hakar7M4_5crQov59hwGNSBlvpDQYZA' },
        { title: 'GT9 / GTX / GT7 / 38', id: '1lHBUOIIZShnFzl1u1qxiAQ_8JqAcoUyYtHfKz8SOFHY' },
        { title: '45 / 47 / XPD', id: '1ivLUDEIaQ2v0mE52vspdF_YWAVxSS6vqZt_OUnq58yc' },
      ]}
    />
  );
}

// smartsheet sheets (Service / deck / Loading) — Smartsheet blocks framing, so these launch out.
export function ServiceSheet() {
  return (<><PageHeader title="Service 24-26" /><LinkCard title="Service Smartsheet" url={SS('7880646303108996')} hint="Opens in Smartsheet (new tab)" /></>);
}

/** the person's own Google Calendar. `switcher` is the Calendar page's Rolodex | Google choice, beside the title. */
export function GoogleCalendar({ switcher }: { switcher?: ReactNode }) {
  const { me, loading } = useAuth();
  const isPhone = useIsPhone();
  const [frameLoaded, setFrameLoaded] = useState(false);
  const email = me?.user.email ?? '';
  // seven week columns are unreadable in a phone-wide frame, so phones get the agenda list
  const url = email
    ? `https://calendar.google.com/calendar/embed?src=${encodeURIComponent(email)}&ctz=Europe/Athens&mode=${isPhone ? 'AGENDA' : 'WEEK'}&wkst=2&showTitle=0&showPrint=0&showCalendars=0`
    : '';

  if (loading) return <LoadingState label="Loading calendar…" />;

  if (!url) {
    return (
      <div className="embed">
        <div className="embed-bar"><h1 className="page-title">Calendar</h1>{switcher}</div>
        <div className="embed-body">
          <div className="state error" style={{ position: 'absolute', inset: 0, margin: 0 }}>
            <div className="big">No calendar available</div>
            <div>Your account has no email on file, so a personal calendar can't be shown.</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="embed">
      <div className="embed-bar">
        <h1 className="page-title">Calendar</h1>
        {switcher}
        <div style={{ flex: 1 }} />
        {isPhone ? (
          // on a phone the Google Calendar app is the real way in (and iOS often blanks a framed calendar),
          // so opening it is the primary action, full width above the frame
          <>
            <PageHelpButton />
            <a className="btn btn-primary embed-open" href="https://calendar.google.com/" target="_blank" rel="noopener noreferrer">Open in Google Calendar ↗</a>
            <span className="muted embed-hint">Calendar blank? Open it in Google Calendar.</span>
          </>
        ) : (
          <>
            <span className="muted embed-hint">Your calendar · {email} · Blank? Sign into Google in this browser<span className="embed-hint-arrow" aria-hidden="true"> →</span></span>
            <a className="btn btn-sm" href="https://calendar.google.com/" target="_blank" rel="noopener noreferrer">Open in Google Calendar ↗</a>
            <PageHelpButton />
          </>
        )}
      </div>
      <div className="embed-body">
        {!frameLoaded && (
          <div className="embed-loading"><Spinner /><span>Loading calendar…</span></div>
        )}
        <iframe className="embed-frame" src={url} title="My Calendar" onLoad={() => setFrameLoaded(true)} />
      </div>
    </div>
  );
}

// gmail cant be embedded (X-Frame-Options: DENY on mail.google.com).
export function Gmail() {
  return (
    <>
      <PageHeader title="Gmail" />
      <div className="page-panel" style={{ maxWidth: 520 }}>
        <a className="btn btn-primary" href="https://mail.google.com/" target="_blank" rel="noopener noreferrer">Open Gmail ↗</a>
      </div>
    </>
  );
}
